<template>
    <div>
        <v-list-item id="workspace" class="mt-0 mb-2 py-2" link>
            <template #prepend>
                <div v-if="selected_workspace_favicon_url" class="favicon">
                    <img :src="selected_workspace_favicon_url" />
                </div>
                <div v-else class="favicon d-flex align-center justify-center">
                    <v-icon size="small" color="grey">mdi-upload</v-icon>
                </div>
            </template>

            <v-list-item-title class="font-weight-bold">
                {{ selected_workspace_name }}
            </v-list-item-title>

            <v-list-item-subtitle>
                {{ workspace_subscription_name }}
            </v-list-item-subtitle>

            <template #append>
                <v-icon color="black" icon="mdi-unfold-more-horizontal" size="small" />
            </template>
        </v-list-item>

        <v-menu
            activator="#workspace"
            attach="#workspace"
            location="end"
            offset-x="10"
            :model-value="workspace_menu_open"
            @update:model-value="handleWorkspaceMenuChange"
        >
            <v-list
                id="workspace-select-menu"
                nav
                density="compact"
                rounded
                class="shadow-md"
                min-width="200"
            >
                <v-list-subheader class="font-weight-bold">
                    {{ $t('navigation.workspaces') }}
                </v-list-subheader>
                <template v-if="loading_workspaces">
                    <v-list-item>
                        <template #prepend>
                            <v-progress-circular
                                indeterminate
                                size="23"
                                width="3"
                                class="favicon-small"
                            />
                        </template>
                        <v-list-item-title>
                            {{ $t('navigation.workspaces_loading') }}
                        </v-list-item-title>
                    </v-list-item>
                </template>
                <template v-else>
                    <v-list-item
                        v-for="workspace in workspaces"
                        :key="workspace.id"
                        link
                        density="compact"
                        @click="selectWorkspace(workspace)"
                    >
                        <template #prepend>
                            <div
                                :class="['favicon-small', { 'has-favicon': workspace.favicon_url }]"
                            >
                                <img v-if="workspace.favicon_url" :src="workspace.favicon_url" />
                                <v-icon v-else size="small" color="grey">mdi-upload</v-icon>
                            </div>
                        </template>
                        <v-list-item-title>
                            {{ workspace.name }}
                        </v-list-item-title>
                    </v-list-item>
                </template>

                <v-divider class="my-2" />

                <v-list-item
                    id="add-workspace"
                    density="compact"
                    @click="openNewWorkspaceDialog = true"
                >
                    <template #prepend>
                        <div class="favicon-small d-flex align-center justify-center">
                            <v-icon size="x-small">mdi-plus</v-icon>
                        </div>
                    </template>
                    <v-list-item-title>
                        <span class="text-grey-darken-1">
                            {{ $t('workspace.selection.create_workspace_button') }}
                        </span>
                    </v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>

        <CreateWorkspaceDialog
            v-model="openNewWorkspaceDialog"
            @workspace-created="handleWorkspaceCreated"
        />
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useWorkspaceStore } from '@/stores/workspace';
import { useUserStore } from '@/stores/user';
import { storeToRefs } from 'pinia';
import { mainAxios as axios } from '@/assets/js/axios_config';
import CreateWorkspaceDialog from '@/components/workspace/CreateWorkspaceDialog.vue';

// Store initialization
const workspaceStore = useWorkspaceStore();
const userStore = useUserStore();

// Extract reactive refs from stores using storeToRefs
const { selected_workspace } = storeToRefs(userStore);

// Reactive state
const workspace_menu_open = ref(false);
const openNewWorkspaceDialog = ref(false);
const workspaces = ref([]);
const loading_workspaces = ref(false);

// Computed properties
const selected_workspace_name = computed(() => selected_workspace.value?.name);
const workspace_subscription_name = computed(() => selected_workspace.value?.subscription_name);
const selected_workspace_favicon_url = computed(() => selected_workspace.value?.favicon_url);

// Methods
const handleWorkspaceMenuChange = (isOpen) => {
    workspace_menu_open.value = isOpen;
    if (isOpen && workspaces.value.length === 0) {
        getMyWorkspaces();
    }
};

const handleWorkspaceCreated = () => {
    getMyWorkspaces();
};

const getMyWorkspaces = async () => {
    loading_workspaces.value = true;
    try {
        const { data } = await axios.get('/workspace/user/my-workspaces');
        workspaces.value = data;
    } catch (error) {
        console.error('Failed to fetch workspaces:', error);
    } finally {
        loading_workspaces.value = false;
    }
};

const selectWorkspace = async (workspace) => {
    try {
        await axios.post(`/workspace/user/select/${workspace.id}`);
        await resetWorkspace();
        workspace_menu_open.value = false;
    } catch (error) {
        console.error('Failed to select workspace:', error);
    }
};

const resetWorkspace = async () => {
    await workspaceStore.handleWorkspaceChange();
};
</script>

<style lang="scss" scoped>
.favicon {
    width: 30px;
    height: 30px;
    margin-right: 8px;
    border-radius: 6px;
    border: 1px solid #e0e0e0;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        overflow-clip-margin: unset;
        overflow: visible;
    }
}

#workspace {
    :deep(.v-list-item__append) {
        max-width: 20px;
    }

    :deep(.v-list-subheader) {
        min-height: 24px;
    }

    :deep(.v-list-subheader__text) {
        font-size: 10px;
        font-weight: 600;
    }
}

.favicon-small {
    width: 24px;
    height: 24px;
    margin-right: 8px;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        overflow-clip-margin: unset;
        overflow: visible;
    }
}
</style>
